<template>
	<aside class="provider" v-if="data">
    <a :href="data.clinicUrl" class="provider__link">
	      <p class="location-name">{{ locationName }}</p>
        <p class="location">{{ locationLabel }}</p>
        <img class="image" :alt="data.hcpName" :src="data.imageUrl" />
        <div class="who">
            <label class="name">{{ data.hcpName }}</label>
            <label class="title">Hearing Care Practitioner</label>
        </div>
        <div class="information">
            <p class="distance" v-if="milesAway"><strong>{{ milesAway }}</strong> Miles Away</p>
            <template v-if="nextAvailableAppointment">
                <p class="nextAppointment">Next Available Appointment</p>
                <p class="appointmentTime">{{ nextAvailableAppointment }}</p>
            </template>
            <template v-else>
                <p class="nextAppointment">&nbsp;</p>
                <p class="appointmentTime">&nbsp;</p>
            </template>
        </div>
    </a>
		<div class="footer-actions">
      <a v-if="schedulingPhoneNumber" class="btn-beltone dark" :href="schedulingPhoneHref" target="_top">Call {{ schedulingPhoneNumber }} to Schedule</a>
			<button @click="showScheduling(data.clinicUrl)" class="btn-beltone">Book Online</button>
		</div>
	</aside>
</template>

<script>
import {mapState} from 'vuex';
import Services from '../../mixins/services';
import moment from "moment";
import SchedulingModal from './scheduling-modal';
import clinicOverrideName from './clinic-override-name';
import getMichiganClinicPhoneNumbers from './michigan-clinic-phone-numbers';
import { isBeltoneMichiganDealer } from '../../dealer-constants';
import { isGoogleAdSession } from '../../google-ad-attribution';

const normalizeLocationName = (value) => value?.toUpperCase().replace(/,/g, '').replace(/\s+/g, ' ').trim();
const GOOGLE_AD_SESSION_REFRESH_INTERVAL_MS = 250;
const GOOGLE_AD_SESSION_REFRESH_MAX_ATTEMPTS = 24;

export default {
	mixins: [Services],
	props: ['data', 'dealerId'],
	components: {
	},
	data: function () {
		return {
      sheetPhoneNumbers: null,
      sheetPhoneNumbersLoaded: false,
      useGoogleAdSwapNumber: false,
      googleAdSessionRefreshTimer: null,
      googleAdSessionRefreshAttempts: 0
		}
	},
	computed: mapState({
		geoLocation: state => state.LocationSearch.geoLocation,
		globals: state => state.LocationSearch.globals,
		milesAway() {
			return Math.round(this.data.milesAway * 100) / 100
		},
			nextAvailableAppointment() {
				if (this.data.nextAvailableAppointment === '0001-01-01T00:00:00') {
					return null;
				}
				let _dateTime = this.data.nextAvailableAppointment;
				return moment(_dateTime).format('dddd, MMM. Do @ h:mm a');
      },
      locationLabel() {
        let override = clinicOverrideName(this.data.id);
        if (override?.locationLabel !== undefined) {
          return override.locationLabel;
        }
        return `${this.data.city.toUpperCase()}, ${this.data.state.toUpperCase()}`;
      },
      schedulingPhoneNumber() {
        const isMichiganDealer = isBeltoneMichiganDealer(this.dealerId);

        if (this.sheetPhoneNumbers) {
          if (this.useGoogleAdSwapNumber && this.sheetPhoneNumbers.swapNumber) {
            return this.sheetPhoneNumbers.swapNumber;
          }

          return this.sheetPhoneNumbers.phoneNumber;
        }

        if (isMichiganDealer) return null;

        return this.helpers().callCenterPhoneNumber();
      },
      schedulingPhoneHref() {
        const digits = String(this.schedulingPhoneNumber || '').replace(/\D/g, '');

        if (!digits) return null;

        const internationalDigits = digits.length === 10 ? `1${digits}` : digits;
        return `tel:+${internationalDigits}`;
      },
			locationName() {
        let override = clinicOverrideName(this.data.id);
        if (override?.locationName !== undefined) {
          return override.locationName;
        }

      let name = normalizeLocationName(this.data.name);
      let city = normalizeLocationName(this.data.city);
      let state = normalizeLocationName(this.data.state);
      let cityState = `${city} ${state}`.replace(/\s+/g, ' ').trim();

      if (!name) {
        return "";
      }
      if (name === city || name === cityState) {
        return "";
      }
      return this.data.name?.toUpperCase();
		}
	}),
	methods: {
    showScheduling(url) {
      SchedulingModal.open({ dealerId: this.dealerId, clinicUrl: url });
    },
    refreshGoogleAdSession() {
      if (isGoogleAdSession()) {
        this.useGoogleAdSwapNumber = true;
      }

      return this.useGoogleAdSwapNumber;
    },
    stopGoogleAdSessionRefresh() {
      if (this.googleAdSessionRefreshTimer) {
        clearInterval(this.googleAdSessionRefreshTimer);
        this.googleAdSessionRefreshTimer = null;
      }
    },
    startGoogleAdSessionRefresh() {
      this.stopGoogleAdSessionRefresh();
      this.googleAdSessionRefreshAttempts = 0;

      this.googleAdSessionRefreshTimer = setInterval(() => {
        this.googleAdSessionRefreshAttempts += 1;

        if (
          this.refreshGoogleAdSession() ||
          this.googleAdSessionRefreshAttempts >= GOOGLE_AD_SESSION_REFRESH_MAX_ATTEMPTS
        ) {
          this.stopGoogleAdSessionRefresh();
        }
      }, GOOGLE_AD_SESSION_REFRESH_INTERVAL_MS);
    },
    setMichiganClinicPhoneNumber() {
      this.refreshGoogleAdSession();
      this.sheetPhoneNumbers = null;
      this.sheetPhoneNumbersLoaded = false;

      if (!isBeltoneMichiganDealer(this.dealerId) || !this.data?.id) {
        this.sheetPhoneNumbersLoaded = true;
        this.stopGoogleAdSessionRefresh();
        return;
      }

      if (!this.useGoogleAdSwapNumber) {
        this.startGoogleAdSessionRefresh();
      }

      getMichiganClinicPhoneNumbers().then((phoneNumbers) => {
        this.refreshGoogleAdSession();
        this.sheetPhoneNumbers = phoneNumbers[String(this.data.id)] || null;
        this.sheetPhoneNumbersLoaded = true;

        if (!this.useGoogleAdSwapNumber) {
          this.startGoogleAdSessionRefresh();
        }
      });
    }
	},
  watch: {
    'data.id': function () {
      this.setMichiganClinicPhoneNumber();
    },
    dealerId: function () {
      this.setMichiganClinicPhoneNumber();
    }
  },
	mounted() {
    this.setMichiganClinicPhoneNumber();
	},
  beforeDestroy() {
    this.stopGoogleAdSessionRefresh();
  },
	created() {
	}
}
</script>
